(() => {
  if (customElements.get('incubalia-horizontal-tabs')) return;

  class IncubaliaHorizontalTabs extends HTMLElement {
    connectedCallback() {
      this.details = Array.from(this.querySelectorAll('[data-horizontal-tabs-item]'));
      this.mediaHost = this.querySelector('[data-horizontal-tabs-media-host]');
      this.abortController = new AbortController();
      this.isSyncing = false;
      this.timer = null;
      this.activeMediaKey = '';
      this.autorotateEnabled = this.dataset.autorotate === 'true';
      this.autorotateSpeed = parseInt(this.dataset.autorotateSpeed || '0', 10);
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.details.length) return;

      if (!this.details.some((details) => details.open)) {
        this.details[0].open = true;
      }

      this.details.forEach((details) => {
        details.addEventListener('toggle', () => this.onToggle(details), {
          signal: this.abortController.signal,
        });
      });

      this.addEventListener('pointerenter', () => this.stopAutorotate(), {
        signal: this.abortController.signal,
      });
      this.addEventListener('pointerleave', () => this.startAutorotate(), {
        signal: this.abortController.signal,
      });
      this.addEventListener('focusin', () => this.stopAutorotate(), {
        signal: this.abortController.signal,
      });
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) this.startAutorotate();
        },
        { signal: this.abortController.signal }
      );

      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), {
        signal: this.abortController.signal,
      });
      document.addEventListener('shopify:block:deselect', () => this.startAutorotate(), {
        signal: this.abortController.signal,
      });

      this.syncMedia();
      this.startAutorotate();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.stopAutorotate();
    }

    onToggle(activeDetails) {
      if (this.isSyncing) return;

      if (activeDetails.open) {
        this.closeOthers(activeDetails);
      } else if (!this.details.some((details) => details.open)) {
        this.openByIndex(this.details.indexOf(activeDetails), false);
        return;
      }

      this.syncMedia();
      this.restartAutorotate();
    }

    closeOthers(activeDetails) {
      this.isSyncing = true;

      this.details.forEach((details) => {
        if (details !== activeDetails) details.open = false;
      });

      this.isSyncing = false;
    }

    openByIndex(index, shouldScroll = true) {
      const nextIndex = Math.min(Math.max(index, 0), this.details.length - 1);

      this.isSyncing = true;
      this.details.forEach((details, detailsIndex) => {
        details.open = detailsIndex === nextIndex;
      });
      this.isSyncing = false;

      this.syncMedia();

      if (shouldScroll && this.details[nextIndex]) {
        this.details[nextIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      this.restartAutorotate();
    }

    syncMedia() {
      const openDetails = this.details.find((details) => details.open);
      if (!openDetails || !this.mediaHost) return;

      const nextMediaKey = openDetails.dataset.horizontalTabsMediaKey || '';
      const template = openDetails.querySelector('[data-horizontal-tabs-media-template]');

      if (!template) return;
      if (this.activeMediaKey === nextMediaKey && this.mediaHost.childElementCount > 0) return;

      this.mediaHost.replaceChildren(template.content.cloneNode(true));
      this.activeMediaKey = nextMediaKey;
    }

    startAutorotate() {
      if (
        !this.autorotateEnabled ||
        this.autorotateSpeed < 1000 ||
        this.details.length < 2 ||
        this.prefersReducedMotion.matches
      ) {
        return;
      }

      this.stopAutorotate();
      this.timer = window.setInterval(() => this.openNext(), this.autorotateSpeed);
    }

    stopAutorotate() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restartAutorotate() {
      this.stopAutorotate();
      this.startAutorotate();
    }

    openNext() {
      const currentIndex = this.details.findIndex((details) => details.open);
      const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % this.details.length;
      this.openByIndex(nextIndex, false);
    }

    onBlockSelect(event) {
      const selectedBlock = event.target.closest('[data-horizontal-tabs-item]');
      if (!selectedBlock || !this.contains(selectedBlock)) return;

      const index = this.details.indexOf(selectedBlock);
      if (index < 0) return;

      this.openByIndex(index);
      this.stopAutorotate();
    }
  }

  customElements.define('incubalia-horizontal-tabs', IncubaliaHorizontalTabs);
})();
